import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailOtp } from '../services/emailVerificationService';

// Password strength scorer
const getStrength = (pw) => {
  if (!pw) return { level: 0, label: '', color: 'bg-slate-200' };
  let s = 0;
  if (pw.length >= 6)  s++;
  if (pw.length >= 10) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^a-zA-Z\d]/.test(pw)) s++;
  if (s <= 2) return { level: s, label: 'Weak',   color: 'bg-red-500' };
  if (s <= 3) return { level: s, label: 'Fair',   color: 'bg-amber-500' };
  if (s <= 4) return { level: s, label: 'Good',   color: 'bg-blue-500' };
  return            { level: s, label: 'Strong', color: 'bg-emerald-500' };
};

const Register = () => {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]         = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPw, setShowPw]     = useState(false);
  const [showCf, setShowCf]     = useState(false);
  const [error,  setError]      = useState('');
  const [loading, setLoading]   = useState(false);

  const pw = getStrength(form.password);
  const pwsMatch = form.confirm && form.password === form.confirm;

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (form.name.trim().length < 2)    return 'Please enter your full name.';
    if (form.password.length < 6)       return 'Password must be at least 6 characters.';
    if (form.password !== form.confirm) return 'Passwords do not match.';
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      // Step 1: Create Firebase Auth account.
      await register(form.email, form.password, form.name.trim());

      // Step 2: Send verification OTP via SMTP Cloud Function.
      try {
        await sendEmailOtp();
      } catch (otpErr) {
        if (import.meta.env.DEV) console.warn('[Register] sendEmailOtp failed:', otpErr?.message || otpErr);
      }

      // Step 3: Redirect to check-email page.
      navigate('/verify-otp', {
        replace: true,
        state: { email: form.email },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await loginWithGoogle();
      if (user) navigate('/app');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const GoogleIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex">

      {/* ── Left brand panel ──────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600
        flex-col justify-center px-16 py-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white/5" />

        <div className="relative">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center
            justify-center mb-8 shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Start finding<br />leads for free.
          </h1>
          <p className="text-indigo-200 text-lg max-w-sm leading-relaxed mb-10">
            Create your account and get <strong className="text-white">100 free credits</strong> instantly.
            No credit card required.
          </p>

          <ul className="space-y-3 max-w-xs">
            {[
              '100 free credits on sign-up',
              'Search any city or neighbourhood worldwide',
              'Export leads to Excel & PDF',
              'Organise leads into custom lists',
            ].map(item => (
              <li key={item} className="flex items-center gap-3 text-indigo-100 text-sm">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-none">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────── */}
      <div className="flex-1 max-w-xl flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 overflow-y-auto bg-white dark:bg-[#0A0A0A]">

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-10">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600
            flex items-center justify-center shadow-sm">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <span className="text-base font-bold text-slate-900 dark:text-white">Lead Finder</span>
        </div>

        <div className="w-full max-w-sm mx-auto lg:mx-0">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Create your account</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mb-8">Join thousands finding leads globally</p>

          {/* Error */}
          {error && (
            <div className="mb-5 flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-xl p-4">
              <AlertCircle className="w-4 h-4 text-red-500 flex-none mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-4">

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input name="name" type="text" value={form.name} onChange={handleChange}
                  placeholder="Jane Smith" required disabled={loading}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-slate-800 dark:text-white
                    placeholder-slate-400 dark:placeholder-gray-500
                    focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400
                    disabled:opacity-60 transition" />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="you@example.com" required disabled={loading}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-slate-800 dark:text-white
                    placeholder-slate-400 dark:placeholder-gray-500
                    focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400
                    disabled:opacity-60 transition" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input name="password" type={showPw ? 'text' : 'password'} value={form.password}
                  onChange={handleChange} placeholder="Min 6 characters" required disabled={loading}
                  autoComplete="new-password"
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-slate-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-slate-800 dark:text-white
                    placeholder-slate-400 dark:placeholder-gray-500
                    focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400
                    disabled:opacity-60 transition [&::-ms-reveal]:hidden" />
                {form.password && (
                  <button type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {/* Strength bar */}
              {form.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all
                        ${i <= pw.level ? pw.color : 'bg-slate-200 dark:bg-white/10'}`} />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${
                    pw.label === 'Weak'   ? 'text-red-600'     :
                    pw.label === 'Fair'   ? 'text-amber-600'   :
                    pw.label === 'Good'   ? 'text-blue-600'    : 'text-emerald-600'
                  }`}>{pw.label}</p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1.5">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input name="confirm" type={showCf ? 'text' : 'password'} value={form.confirm}
                  onChange={handleChange} placeholder="••••••••" required disabled={loading}
                  autoComplete="new-password"
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-slate-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-slate-800 dark:text-white
                    placeholder-slate-400 dark:placeholder-gray-500
                    focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400
                    disabled:opacity-60 transition [&::-ms-reveal]:hidden" />
                {form.confirm && pwsMatch
                  ? <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                  : form.confirm && (
                    <button type="button" tabIndex={-1} onClick={() => setShowCf(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showCf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )
                }
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" required
                className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-xs text-slate-500 dark:text-gray-400">
                I agree to the{' '}
                <a href="#" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">Privacy Policy</a>
              </span>
            </label>

            <button type="submit" disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white
                font-semibold py-2.5 rounded-lg text-sm transition shadow-sm hover:shadow-md
                flex items-center justify-center gap-2">
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account&hellip;</>
                : 'Create account'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200 dark:border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-slate-50 dark:bg-[#0A0A0A] text-xs text-slate-400 dark:text-gray-500">or sign up with</span>
            </div>
          </div>

          {/* Google */}
          <button onClick={handleGoogleRegister} disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-slate-300 dark:border-white/10
              rounded-lg text-sm font-medium text-slate-700 dark:text-gray-300 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10
              disabled:opacity-50 transition shadow-sm">
            <GoogleIcon />
            Continue with Google
          </button>

          <p className="text-center text-sm text-slate-500 dark:text-gray-400 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
